import {
  Box,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
} from "@mui/material";
import EditLineIcon from "remixicon-react/EditLineIcon";
import DeleteBin2LineIcon from "remixicon-react/DeleteBin2LineIcon";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import EqualIcon from "@mui/icons-material/DragHandle";
import { Fragment, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import Upload2LineIcon from "remixicon-react/Upload2LineIcon";
import { Text } from "$components";
import useIsProductDeletable from "$hooks/useIsProductDeletable";
import { FieldValues } from "$hooks/useReferencingForm";
import { Order, Reception, Suborder } from "$types";
import ReferenceType from "$types/ReferenceType";
import Title from "./Title";
import { theme } from "$utils/theme/bataillog";
import { useStyles } from "./useStyles";
import { normalizeArray } from "$utils/utils";
import { ReceptionReference } from "$types/ReceptionReference";
import { EDBService } from "$services";

type Props = {
  editedBlocks: { id: string; type: string }[];
  addToEditedBlocks: (item: { id: string; type: string }) => void;
  allowToDelete: boolean;
  allowToEdit: boolean;
  blockId: string;
  companyId: string;
  hasReferencingCategory: boolean;
  isReadOnly: boolean;
  setIndexToDelete: (index: number) => void;
  nestIndex: number;
  onDelete: (index: number) => void;
  onReferenceTypeChange: (newReferenceType: ReferenceType) => void;
  productReference?: string;
  productReferences?: ReceptionReference[];
  reception?: Reception;
  onCancelEdit: (_id: string, index: number, type: ReferenceType) => void;
  orders: Order[];
  suborders: Suborder[];
  value: ReferenceType;
  downloadOrderReference?: () => void;
  createOrderReference?: () => void;
  showLinks?: boolean;
};

/**
 * If a productReference and a reception are provided, the Header will check if it is deletable.
 * Otherwise, it is always deletable.
 */
function Header({
  addToEditedBlocks,
  editedBlocks,
  allowToDelete,
  allowToEdit,
  blockId,
  companyId,
  hasReferencingCategory,
  isReadOnly,
  nestIndex,
  setIndexToDelete,
  onCancelEdit,
  onDelete,
  onReferenceTypeChange,
  orders,
  productReference,
  productReferences,
  reception,
  suborders,
  value,
  downloadOrderReference,
  createOrderReference,
  showLinks = false,
}: Props) {
  const classes = useStyles();
  const { getValues, setValue } = useFormContext<FieldValues>();
  const { PRODUCT, ORDER } = ReferenceType;
  const { isDeletable: isProductDeletable } = useIsProductDeletable();

  const [isDeletable, setIsDeletable] = useState<boolean>(!productReference);

  // For reception references
  const [edbIdForReception, setEdbIdForReception] = useState<string | null>(
    null
  );
  const [articleInSuivi, setArticleInSuivi] = useState(false);

  useEffect(() => {
    const doGetIsDeletable = async () => {
      const res = await isProductDeletable(
        productReference,
        companyId,
        reception.id
      );
      setIsDeletable(res);
    };
    if (reception?.id && productReference) {
      doGetIsDeletable();
    }
  }, [companyId, isProductDeletable, reception?.id, productReference]);

  useEffect(() => {
    const fetchEdbForReception = async () => {
      if (productReferences?.length > 0 && reception?.id) {
        const receptionRefIds = productReferences
          .map((pr) => pr.id)
          .filter(Boolean);
        if (receptionRefIds.length > 0) {
          const edbData =
            await EDBService.isEdbExistForProduct(receptionRefIds);
          if (edbData?.id) {
            setEdbIdForReception(edbData.id);
          }
        }
      }
    };
    fetchEdbForReception();
  }, [productReferences, reception?.id]);

  useEffect(() => {
    const fetchEdbData = async () => {
      if (edbIdForReception) {
        try {
          const dataEdb = await EDBService.getEdbById(edbIdForReception);

          const receptionRefIds = productReferences
            .map((pr) => pr.id)
            .filter(Boolean);

          dataEdb.products.forEach((product: any) => {
            if (receptionRefIds.includes(product.receptionReferenceId)) {
              setArticleInSuivi(true);
            }
          });
        } catch (error) {
          console.error("Error fetching EDB data:", error);
        }
      }
    };

    fetchEdbData();
  }, [edbIdForReception, productReferences]);

  const toggleValue = () => {
    onReferenceTypeChange(value === PRODUCT ? ORDER : PRODUCT);
  };

  const copyAllReferences = () => {
    const list = getValues(`productReferenceLists.${nestIndex}`);
    const { productQuantity } = reception;
    setValue(`productReferenceLists.${nestIndex}`, {
      ...list,
      productReferences: list.productReferences.map((p) => {
        const pq = productQuantity.find((pq) =>
          // Check all suborders that this product belongs to
          p.suborders.some(
            (suborder) =>
              pq.suborderReference === suborder.reference &&
              pq.productId === p.originalProductReference
          )
        );
        return {
          ...p,
          productReference:
            // don't copy value for product not received
            pq && pq.receivedQuantity === 0 ? "" : p.originalProductReference,
        };
      }),
    });
  };

  const deleteAllNewReferences = () => {
    const list = getValues(`productReferenceLists.${nestIndex}`);

    setValue(`productReferenceLists.${nestIndex}`, {
      ...list,
      productReferences: list.productReferences.map((p) => ({
        ...p,
        productReference: "",
      })),
    });
  };

  const cancelEdit = () => {
    const differentTypeBlock = editedBlocks.find(
      (block) => block.id === blockId && block.type !== value
    );

    if (differentTypeBlock) {
      onReferenceTypeChange(differentTypeBlock.type as ReferenceType);
    }
    onCancelEdit(blockId, nestIndex, value);
  };

  const plural = suborders?.length > 1 ? "s" : "";
  const noun =
    normalizeArray(orders) &&
    suborders?.every((s) => {
      const order = normalizeArray(orders).find((o) =>
        o?.suborders?.some((s2) => s2.id === s.id)
      );
      const orderSuborders = order?.suborders;
      return orderSuborders?.length === 1;
    })
      ? "Commande"
      : "Sous-commande";

  return (
    <div className={classes.wrapper}>
      <Box className={classes.titleBlock}>
        <Text type="text" className={classes.title}>
          {`${noun}${plural} :`}
        </Text>
        {(allowToEdit || allowToDelete) && (
          <Box className={classes.buttons}>
            {allowToEdit &&
              (isReadOnly ? (
                <IconButton
                  className={classes.topButton}
                  disabled={!isDeletable || articleInSuivi}
                  onClick={() =>
                    addToEditedBlocks({ id: blockId, type: value })
                  }
                  size="small"
                >
                  <EditLineIcon />
                </IconButton>
              ) : (
                // eslint-disable-next-line react/no-unescaped-entities
                <Button onClick={cancelEdit}>Annuler l'édition</Button>
              ))}
            <IconButton
              className={classes.topButton}
              disabled={!isDeletable || !allowToDelete || articleInSuivi}
              onClick={() => onDelete(nestIndex)}
              size="small"
            >
              <DeleteBin2LineIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      <Box className={classes.suborders}>
        {normalizeArray(orders) &&
          suborders?.map((suborder, i) => {
            const order = normalizeArray(orders).find((o) =>
              o?.suborders?.map((s) => s.reference).includes(suborder.reference)
            );

            // Add null check before accessing order properties
            if (!order) {
              return (
                <Fragment key={suborder.id}>
                  <Title customReference={undefined} suborder={suborder} />
                  {i !== (suborders?.length ?? 0) - 1 && (
                    <Text className={classes.pipeStyle}>&nbsp;|&nbsp;</Text>
                  )}
                </Fragment>
              );
            }

            return (
              <Fragment key={suborder.id}>
                <Title
                  customReference={
                    order?.suborders?.length === 1 ? order.reference : undefined
                  }
                  suborder={suborder}
                />
                {i !== (suborders?.length ?? 0) - 1 && (
                  <Text className={classes.pipeStyle}>&nbsp;|&nbsp;</Text>
                )}
              </Fragment>
            );
          })}
        {showLinks && (
          <Box flex={1}>
            <Button
              style={{
                color: theme.color.blueLight,
                minWidth: "initial",
              }}
              type="button"
              color="primary"
              className={classes.linkButton}
              onClick={downloadOrderReference}
            >
              <Upload2LineIcon className={classes.iconStyle} />
              Réception référencement Stock-it
            </Button>
            <Button
              style={{
                color: theme.color.blueLight,
                minWidth: "initial",
              }}
              type="button"
              color="primary"
              className={classes.linkButton}
              onClick={createOrderReference}
            >
              <Upload2LineIcon className={classes.iconStyle} />
              Création référencement Stock-it
            </Button>
          </Box>
        )}
      </Box>

      <Box className={classes.actions}>
        <RadioGroup
          aria-label="referenceType"
          row
          name="reference-type"
          value={value}
          onChange={toggleValue}
        >
          <FormControlLabel
            disabled={!hasReferencingCategory || isReadOnly}
            value={ORDER}
            control={<Radio color="primary" />}
            label="À la réception"
          />
          <FormControlLabel
            disabled={isReadOnly}
            value={PRODUCT}
            onChange={() => setIndexToDelete(nestIndex)}
            control={<Radio color="primary" />}
            label="À l'article"
          />
        </RadioGroup>
        {value === ReferenceType.PRODUCT &&
          hasReferencingCategory &&
          !isReadOnly && (
            <Box>
              <Button
                onClick={deleteAllNewReferences}
                size="small"
                variant="contained"
              >
                <ClearRoundedIcon />
              </Button>
              <Button
                className={classes.equalAllBtn}
                onClick={copyAllReferences}
                size="small"
                variant="contained"
              >
                <EqualIcon />
              </Button>
            </Box>
          )}
      </Box>
    </div>
  );
}

export default Header;
